import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { observer } from 'mobx-react';
import { Modal, Autocomplete, Typography, Button, CircularProgress } from '@ann/web-components';
import { NameInitialsAvatar } from 'react-name-initials-avatar';
import { useStore } from '../../../hooks';
import { useToast } from '../../../components/toast';
import { getAvatarUrl, generateAvatarColor } from '../../../utils/avatarUtils';
import { getModuleInitialRoute } from '../../../utils/routeUtils';
import { getWorkspaceList } from '../../../services/workspace';
import websocketService from '../../../services/websocketService';
import {
  AutocompleteContainer,
  ModalBodyContent,
  WorkspaceListContainer,
  WorkspaceItem,
  WorkspaceItemLeft,
  WorkspaceItemRight,
  WorkspaceAvatar,
  WorkspaceAvatarWithInitials,
  CurrentWorkspaceText,
  LoadingMoreContainer,
  ScrollSentinel,
} from './SwitchWorkspaceModal.styled';

const PAGE_SIZE = 15;

const SwitchWorkspaceModal = ({ isOpen, onClose, onWorkspaceSelect }) => {
  const { workspaceStore, authStore } = useStore();
  const toast = useToast();
  const location = useLocation();

  const [workspaces, setWorkspaces] = useState([]);
  const [meta, setMeta] = useState(null);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [autocompleteKey, setAutocompleteKey] = useState(0);
  const [failedImages, setFailedImages] = useState(new Set());

  const autocompleteRef = useRef(null);
  const sentinelRef = useRef(null);
  const listContainerRef = useRef(null);
  const searchTimeoutRef = useRef(null);
  const fetchIdRef = useRef(0);
  const currentPageRef = useRef(1);

  const handleImageError = useCallback((workspaceId) => {
    setFailedImages((prev) => new Set([...prev, workspaceId]));
  }, []);

  const getWorkspaceId = useCallback((workspace) => {
    if (!workspace) return null;

    const id = workspace.tenant_uuid ||
               workspace.tenantUuid ||
               workspace.tenant_id ||
               workspace.tenantId ||
               workspace.id ||
               workspace.workspaceUuid ||
               workspace.uuid ||
               null;

    return id ? String(id) : null;
  }, []);

  const readyWorkspaces = useMemo(() => {
    return workspaces.filter((ws) => ws && String(ws?.status ?? "").toLowerCase() === "ready");
  }, [workspaces]);

  const fetchWorkspaces = useCallback(async (page, keyword, append = false) => {
    const requestId = ++fetchIdRef.current;
    const isInitial = !append;
    isInitial ? setLoading(true) : setLoadingMore(true);

    try {
      const params = {
        page,
        page_size: PAGE_SIZE,
        order_by: ["created_at"],
        order_direction: ["desc"],
      };

      if (keyword?.trim()) {
        params.search = { keyword: keyword.trim() };
      }

      const response = await getWorkspaceList(params);

      if (requestId !== fetchIdRef.current) return;

      const list = Array.isArray(response.body) ? response.body : [];

      setWorkspaces((prev) => append ? [...prev, ...list] : list);
      setMeta(response.meta || null);
    } catch (err) {
      if (requestId !== fetchIdRef.current) return;
      console.error('Error fetching workspaces:', err);
    } finally {
      if (requestId === fetchIdRef.current) {
        isInitial ? setLoading(false) : setLoadingMore(false);
      }
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      currentPageRef.current = 1;
      setSearchKeyword('');
      setWorkspaces([]);
      setMeta(null);
      setAutocompleteKey((prev) => prev + 1);
      fetchWorkspaces(1, '');
    }

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && meta?.has_next && !loadingMore && !loading) {
          const nextPage = currentPageRef.current + 1;
          currentPageRef.current = nextPage;
          fetchWorkspaces(nextPage, searchKeyword, true);
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(sentinel);

    return () => {
      observer.disconnect();
    };
  }, [meta, loadingMore, loading, searchKeyword, fetchWorkspaces]);

  const suggestions = useMemo(() => {
    return readyWorkspaces
      .map((workspace) => workspace.tenant_name || workspace.name || '')
      .filter(Boolean);
  }, [readyWorkspaces]);

  const handleSwitchWorkspace = useCallback(async (workspace) => {
    const tenantUuid = workspace.tenant_uuid ||
                       workspace.tenantUuid ||
                       workspace.workspaceUuid ||
                       workspace.workspace_uuid;

    if (tenantUuid && authStore.apiUserId) {
      try {
        await authStore.refreshTokenForWorkspace(tenantUuid);
        websocketService.reconnect();
      } catch (error) {
        console.error('Error switching workspace token:', error);
        toast.error({
          title: 'Unable to switch workspace',
          message: error?.message || 'Something went wrong. Please try again.',
          canClose: true,
        });
        onClose?.();
        return;
      }
    } else {
      console.warn('Could not get tenantUuid or apiUserId:', { tenantUuid, apiUserId: authStore.apiUserId });
    }

    workspaceStore.setCurrentWorkspace(workspace, authStore);

    const workspaceName = workspace?.tenant_name || workspace?.name || workspace?.workspaceName || 'Workspace';
    toast.success({
      title: workspaceName,
      message: "You're now viewing data and settings for this workspace.",
      canClose: true,
    });

    const initialRoute = getModuleInitialRoute(location.pathname);
    window.location.href = initialRoute || '/v2/home';

    if (onWorkspaceSelect) {
      onWorkspaceSelect(workspace);
    }
  }, [onWorkspaceSelect, workspaceStore, authStore, toast, location.pathname, onClose]);

  const handleSearchChange = useCallback((value) => {
    setSearchKeyword(value);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      currentPageRef.current = 1;
      setWorkspaces([]);
      setMeta(null);
      fetchWorkspaces(1, value);
    }, 500);
  }, [fetchWorkspaces]);

  const handleSuggestionSelect = useCallback(async (value) => {
    const selectedWorkspace = readyWorkspaces.find(
      (workspace) => (workspace.tenant_name || workspace.name || workspace.workspaceName) === value
    );

    if (selectedWorkspace) {
      await handleSwitchWorkspace(selectedWorkspace);
    }
  }, [readyWorkspaces, handleSwitchWorkspace]);

  const handleClear = useCallback(() => {
    setSearchKeyword('');
    setAutocompleteKey((prev) => prev + 1);

    if (autocompleteRef.current) {
      const input = autocompleteRef.current.querySelector('input');
      if (input) {
        input.value = '';
        input.dispatchEvent(new Event('input', { bubbles: true }));
      }
    }

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    currentPageRef.current = 1;
    setWorkspaces([]);
    setMeta(null);
    fetchWorkspaces(1, '');
  }, [fetchWorkspaces]);

  const handleClose = useCallback(() => {
    setSearchKeyword('');
    setAutocompleteKey((prev) => prev + 1);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    onClose?.();
  }, [onClose]);

  const isCurrentWorkspace = useCallback((workspace) => {
    const currentWorkspace = workspaceStore.currentWorkspace;

    if (!currentWorkspace || !workspace) {
      return false;
    }

    const currentId = getWorkspaceId(currentWorkspace);
    const workspaceId = getWorkspaceId(workspace);

    if (currentId && workspaceId) {
      if (String(currentId).trim().toLowerCase() === String(workspaceId).trim().toLowerCase()) {
        return true;
      }
    }

    const currentName = (currentWorkspace.tenant_name || currentWorkspace.name || '').trim().toLowerCase();
    const workspaceName = (workspace.tenant_name || workspace.name || '').trim().toLowerCase();
    return currentName && workspaceName && currentName === workspaceName;
  }, [workspaceStore.currentWorkspace, getWorkspaceId]);

  if (!isOpen) {
    return null;
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      width="526px"
      size="medium"
    >
      <Modal.Header>
        <Modal.Header.Title>Switch Workspace</Modal.Header.Title>
      </Modal.Header>
      <Modal.Body>
        <ModalBodyContent>
          <AutocompleteContainer id="switch-workspace-autocomplete">
            <Autocomplete
              key={autocompleteKey}
              ref={autocompleteRef}
              value={searchKeyword}
              onChange={handleSearchChange}
              onSearch={handleSearchChange}
              onSuggestionSelect={handleSuggestionSelect}
              onClear={handleClear}
              placeholder="Search workspace..."
              suggestions={suggestions}
              disabled={loading}
            />
          </AutocompleteContainer>

          <WorkspaceListContainer ref={listContainerRef}>
            {loading && readyWorkspaces.length === 0 ? (
              <LoadingMoreContainer>
                <CircularProgress size="small" />
              </LoadingMoreContainer>
            ) : (
              <>
                {readyWorkspaces.map((workspace, index) => {
                  const workspaceId = getWorkspaceId(workspace);
                  const workspaceName = workspace.tenant_name || workspace.name || '';

                  if (!workspaceId && !workspaceName) {
                    return null;
                  }

                  const photoUrl = workspace.photo_url || workspace.photoUrl;
                  const avatarColor = generateAvatarColor(workspaceName);
                  const imageFailed = workspaceId ? failedImages.has(workspaceId) : false;
                  const isCurrent = isCurrentWorkspace(workspace);
                  const avatarUrl = imageFailed ? null : getAvatarUrl(photoUrl, workspaceName);
                  const showInitialsAvatar = imageFailed || !avatarUrl;
                  const itemKey = workspaceId || `workspace-${index}-${workspaceName}`;

                  return (
                    <WorkspaceItem key={itemKey}>
                      <WorkspaceItemLeft>
                        {!showInitialsAvatar && avatarUrl ? (
                          <WorkspaceAvatar
                            src={avatarUrl}
                            size="lg"
                            onError={() => handleImageError(workspaceId)}
                          />
                        ) : (
                          <WorkspaceAvatarWithInitials>
                            <NameInitialsAvatar
                              name={workspaceName}
                              bgColor={avatarColor}
                              textColor="#ffffff"
                              size="21px"
                              borderRadius="50%"
                              textWeight="bold"
                              textSize="8px"
                              borderWidth="0"
                            />
                          </WorkspaceAvatarWithInitials>
                        )}
                        <Typography
                          category="subtitle"
                          weight="bold"
                          size="small"
                        >
                          {workspaceName}
                        </Typography>
                      </WorkspaceItemLeft>
                      <WorkspaceItemRight>
                        {isCurrent ? (
                          <CurrentWorkspaceText>
                            <Typography
                              category="body"
                              weight="regular"
                              size="small"
                            >
                              Current workspace
                            </Typography>
                          </CurrentWorkspaceText>
                        ) : (
                          <Button
                            iconRight="arrowRight"
                            label="Switch"
                            size="small"
                            type="tertiary"
                            onClick={() => handleSwitchWorkspace(workspace)}
                          />
                        )}
                      </WorkspaceItemRight>
                    </WorkspaceItem>
                  );
                })}

                {loadingMore && (
                  <LoadingMoreContainer>
                    <CircularProgress size="small" />
                  </LoadingMoreContainer>
                )}

                <ScrollSentinel ref={sentinelRef} />
              </>
            )}
          </WorkspaceListContainer>
        </ModalBodyContent>
      </Modal.Body>
    </Modal>
  );
};

export default observer(SwitchWorkspaceModal);
